import type {
  ExamFormTest,
  ExamFormTestProm,
  PromItem,
  PromQrResponse,
  Question,
} from "@rmh/defs";
import { Prom } from "@rmh/defs";
import {
  lowerExtremityFunctionalScaleCalculations,
  getLowerExtremityFunctionalScaleProm,
} from "./lowerExtremityFunctionalScale";
import {
  neckDisabilityIndexCalculations,
  getNeckDisabilityIndexProm,
} from "./neckDisabilityIndex";
import { oswestryCalculations, getOswestryProm } from "./oswestry";
import { quickdashCalculations, getQuickdashProm } from "./quickdash";

export const getPromExamQuestions = (prom: Prom, locale: string = "en") => {
  switch (prom) {
    case Prom.LowerExtremityFunctionalScale:
      return getLowerExtremityFunctionalScaleProm(locale);
    case Prom.NeckDisabilityIndex:
      return getNeckDisabilityIndexProm(locale);
    case Prom.Oswestry:
      return getOswestryProm(locale);
    case Prom.Quickdash:
      return getQuickdashProm(locale);
    default:
      return null;
  }
};
function getRandomNumber(n: number) {
  // Generate a random decimal between 0 (inclusive) and 1 (exclusive)
  const randomDecimal = Math.random();

  // Scale the random decimal to the range [0, n) and round it to the nearest integer
  const randomNumber = Math.floor(randomDecimal * (n + 1));

  return randomNumber;
}

export const generateRandomPromScores = (questions: Question[]) => {
  const finalQuestions: Question[] = questions.map((question) => {
    const length = question.scaleLabels?.length ?? 5;
    const score = getRandomNumber(length - 1);

    return {
      ...question,
      userAnswer: score,
    };
  });

  return finalQuestions;
};

export const getPromCalculations = (prom: Prom) => {
  switch (prom) {
    case Prom.LowerExtremityFunctionalScale:
      return lowerExtremityFunctionalScaleCalculations;
    case Prom.NeckDisabilityIndex:
      return neckDisabilityIndexCalculations;
    case Prom.Oswestry:
      return oswestryCalculations;
    case Prom.Quickdash:
      return quickdashCalculations;
    default:
      return (questions: Question[]) => {
        console.error("No calculation exists for this type of prom", questions);
        return -1;
      };
  }
};

export const getPromTotalScore = (prom: Prom) => {
  switch (prom) {
    case Prom.LowerExtremityFunctionalScale:
      return 80;
    case Prom.NeckDisabilityIndex:
      return 100;
    case Prom.Oswestry:
      return 100;
    case Prom.Quickdash:
      return 100;
    default:
      return -1;
  }
};

export const getPromRangeLabels = (prom: Prom) => {
  switch (prom) {
    case Prom.LowerExtremityFunctionalScale:
      return [
        "Limited function",
        "Some function",
        "Moderate function",
        "Enhanced function",
        "Full function",
      ];
    case Prom.NeckDisabilityIndex:
      return [
        "No disability",
        "Mild disability",
        "Moderate disability",
        "Severe Disability",
        "Complete disability",
      ];
    case Prom.Oswestry:
      return [
        "No disability",
        "Mild disability",
        "Moderate disability",
        "Severe Disability",
        "Complete disability",
      ];
    case Prom.Quickdash:
      return [
        "No disability",
        "Mild disability",
        "Moderate disability",
        "Severe Disability",
        "Complete disability",
      ];
    default:
      return [];
  }
};

export const isPromTest = (test: ExamFormTest): test is ExamFormTestProm => {
  return test.__typename === "ExamFormTestProm";
};

export type PromData = {
  index: number | null;
  updatedAt: string | null;
  ansStartAt: number | null;
  totalScore: number | null;
  score: number | null;
  percentage: number | null;
  promKey: string | null;
  promName: string | null;
  questions: Question[];
};

/**
 * Extract PROM data from a PromItem (from prom.graphql)
 * This reconstructs the full PROM questions with user responses
 */
export const extractPromDataFromPromItem = (
  promItem: PromItem | null | undefined,
  locale: string = "en"
): PromData | null => {
  if (!promItem) {
    return null;
  }

  // Get the question template for this PROM type
  const promExam = getPromExamQuestions(promItem.promKey as Prom, locale);

  if (!promExam) {
    return null;
  }

  const promTest = promExam.examFormTests[0];

  if (!promTest?.questions) {
    return null;
  }

  const questions: Question[] = promTest.questions.map((q, index) => ({
    ...q,
    userAnswer: promItem.responses[index] ?? null,
  }));

  const calculator = getPromCalculations(promItem.promKey as Prom);
  const score = calculator(questions);
  const total = getPromTotalScore(promItem.promKey as Prom);
  const percentage = Math.round((score / total) * 100);

  return {
    index: promItem.index ?? null,
    updatedAt: promItem.completedAt ?? promItem.updatedAt ?? null,
    totalScore: total,
    score: score,
    percentage: percentage,
    promName: promTest.promName ?? null,
    promKey: promItem.promKey ?? null,
    questions: questions,
    ansStartAt: promTest.startAt ?? null,
  };
};

/**
 * Extract PROM data from a Canvas `promQR` response.
 *
 * The Canvas twin of `extractPromDataFromPromItem`: Canvas stores only the
 * numeric answers, so the wording, scale labels and starting offset still come
 * from the static template.
 */
export const extractPromDataFromPromQR = (
  response: PromQrResponse | null | undefined,
  locale: string = "en"
): PromData | null => {
  if (!response) {
    return null;
  }

  const promKey = response.promKey as Prom;
  const promExam = getPromExamQuestions(promKey, locale);

  if (!promExam) {
    return null;
  }

  const promTest = promExam.examFormTests[0];

  if (!promTest?.questions) {
    return null;
  }

  // Scored at write time from a validated, complete response. Recomputing here
  // would coerce a missing answer to 0, which for QuickDASH -- whose scale
  // starts at 1 -- puts the sum below its legal floor.
  const score = response.totalScore;

  // A response we cannot score would render as though the patient scored zero.
  if (score == null) {
    return null;
  }

  const questions: Question[] = promTest.questions.map((q, index) => ({
    ...q,
    userAnswer: response.itemScores[index] ?? null,
  }));

  const total = getPromTotalScore(promKey);

  return {
    index: null,
    updatedAt: response.recordDate ?? null,
    totalScore: total,
    score,
    percentage: Math.round((score / total) * 100),
    promName: promTest.promName ?? null,
    promKey: response.promKey,
    questions,
    ansStartAt: promTest.startAt ?? null,
  };
};
